import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  Add,
  ArrowBack,
  AssignmentTurnedIn,
  AutoStories,
  Block,
  CheckCircle,
  Edit,
  FactCheck,
  Folder,
  Groups,
  History,
  Mic,
  OpenInNew,
  PlayArrow,
  Refresh,
  Replay,
  Save,
  ThumbUp,
  UploadFile,
} from '@mui/icons-material';
import {
  Artifact,
  AuditEvent,
  Book,
  BookDesignTemplate,
  BookExport,
  BookVersion,
  platformApi,
  Project,
  ProjectInterviewSession,
  ProjectMembership,
  PublishingRequest,
  PublishingRequestUpdatePayload,
  Review,
  ReviewComment,
  ReviewCommentStatusCounts,
  StoryChapter,
  StoryOutline,
  Transcript,
  User,
} from '../services/platformApi';
import {
  getStatusColor,
  languageOptions,
  packageTypes,
  projectRoles,
  projectStatuses,
  storyTypes,
} from './projectConstants';
import { ArtifactsTab } from './project-workspace/ArtifactsTab';
import { AuditTab } from './project-workspace/AuditTab';
import { DesignPublishingTab } from './project-workspace/DesignPublishingTab';
import { InterviewStudioTab } from './project-workspace/InterviewStudioTab';
import { WorkflowTab } from './project-workspace/WorkflowTab';
import { WritingStudioTab } from './project-workspace/WritingStudioTab';
import {
  BookDesignFormState,
  applyBookTypeDefaults,
  applyDesignSettingsToForm,
  bookToDesignForm,
  buildBookCover,
  buildBookDesignSettings,
  buildBookManuscript,
  buildBookPrintProfile,
  createBookDesignForm,
  initialBookDesignForm,
} from './project-workspace/bookDesignUtils';
import { BookType } from './project-workspace/manuscriptSchema';
import { reviewCommentStatuses, workflowStages } from './project-workspace/workspaceConfig';
import {
  ArtifactFormState,
  InterviewSessionFormState,
  ProjectEditorState,
  ReviewCommentFormState,
  ReviewRequestFormState,
  TranscriptFormState,
  initialArtifactForm,
  initialInterviewSessionForm,
  initialReviewCommentForm,
  initialReviewCommentStatusCounts,
  initialReviewRequestForm,
  initialTranscriptForm,
} from './project-workspace/workspaceTypes';
import { useLanguage } from '../LanguageContext';
import { formatTranslatedLabel } from '../i18n';
import { useAuth } from '../AuthContext';
import { getRequestErrorMessage } from '../services/api';

const isZoomMeetingLink = (value: string): boolean => {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'https:' && (parsed.hostname === 'zoom.us' || parsed.hostname.endsWith('.zoom.us'));
  } catch {
    return false;
  }
};

const getBookExportFailureMessage = (bookExport: BookExport, fallback: string): string => {
  const reportMessage = bookExport.preflight_report_json?.message;
  if (typeof reportMessage === 'string' && reportMessage.trim()) {
    return reportMessage;
  }
  const renderError = bookExport.metadata_json?.render_error;
  if (renderError && typeof renderError === 'object' && 'message' in renderError) {
    const message = (renderError as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
  }
  return fallback;
};

const editableBookStatuses = ['draft_design', 'layout_review', 'changes_requested', 'approved_for_export'];

const isEditableBookStatus = (status?: string): boolean => editableBookStatuses.includes(status ?? '');

type BookDesignDraftCreateInput = {
  name: string;
  sourceArtifactId?: number;
  bookType: BookType;
  template?: BookDesignTemplate;
};

const getLastBookDesignDraftStorageKey = (projectId: number, userId?: number): string =>
  `talegence.lastBookDesignDraft.${projectId}.${userId ?? 'default'}`;

const ProjectWorkspace: React.FC = () => {
  const { projectId } = useParams();
  const parsedProjectId = Number(projectId);
  const { locale, t } = useLanguage();
  const { currentUser } = useAuth();
  const [project, setProject] = useState<Project | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [memberships, setMemberships] = useState<ProjectMembership[]>([]);
  const [auditEvents, setAuditEvents] = useState<AuditEvent[]>([]);
  const [artifacts, setArtifacts] = useState<Artifact[]>([]);
  const [interviewSessions, setInterviewSessions] = useState<ProjectInterviewSession[]>([]);
  const [zoomLinkDrafts, setZoomLinkDrafts] = useState<Record<number, string>>({});
  const [transcripts, setTranscripts] = useState<Transcript[]>([]);
  const [storyOutlines, setStoryOutlines] = useState<StoryOutline[]>([]);
  const [storyChapters, setStoryChapters] = useState<StoryChapter[]>([]);
  const [books, setBooks] = useState<Book[]>([]);
  const [selectedBookId, setSelectedBookId] = useState('');
  const [bookVersions, setBookVersions] = useState<BookVersion[]>([]);
  const [bookExports, setBookExports] = useState<BookExport[]>([]);
  const [publishingRequests, setPublishingRequests] = useState<PublishingRequest[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewComments, setReviewComments] = useState<ReviewComment[]>([]);
  const [reviewCommentStatusCounts, setReviewCommentStatusCounts] = useState<ReviewCommentStatusCounts>(
    initialReviewCommentStatusCounts
  );
  const [reviewCommentStatusFilter, setReviewCommentStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('settings');
  const [assignTeamDialogOpen, setAssignTeamDialogOpen] = useState(false);
  const [assignUserId, setAssignUserId] = useState('');
  const [assignRole, setAssignRole] = useState('service_provider');
  const [editor, setEditor] = useState<ProjectEditorState | null>(null);
  const [artifactForm, setArtifactForm] = useState<ArtifactFormState>(initialArtifactForm);
  const [artifactFile, setArtifactFile] = useState<File | null>(null);
  const [interviewSessionForm, setInterviewSessionForm] = useState<InterviewSessionFormState>(
    initialInterviewSessionForm
  );
  const [transcriptForm, setTranscriptForm] = useState<TranscriptFormState>(initialTranscriptForm);
  const [editingTranscriptId, setEditingTranscriptId] = useState<number | null>(null);
  const [bookForm, setBookForm] = useState<BookDesignFormState>(initialBookDesignForm);
  const [reviewRequestForm, setReviewRequestForm] = useState<ReviewRequestFormState>(initialReviewRequestForm);
  const [reviewDecisionNotes, setReviewDecisionNotes] = useState<Record<number, string>>({});
  const [reviewCommentForm, setReviewCommentForm] = useState<ReviewCommentFormState>(initialReviewCommentForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [artifactUploadError, setArtifactUploadError] = useState<string | null>(null);

  const loadProject = useCallback(async () => {
    if (!Number.isFinite(parsedProjectId)) {
      setError(t('invalidProjectId'));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const [
        projectData,
        userData,
        membershipData,
        auditData,
        artifactData,
        sessionData,
        transcriptData,
        storyOutlineData,
        storyChapterData,
        bookData,
        reviewData,
        reviewCommentData,
        reviewCommentStatusCountData,
      ] = await Promise.all([
        platformApi.getProject(parsedProjectId),
        platformApi.listUsers(),
        platformApi.listProjectMemberships(parsedProjectId),
        platformApi.listProjectAuditEvents(parsedProjectId),
        platformApi.listProjectArtifacts(parsedProjectId),
        platformApi.listProjectInterviewSessions(parsedProjectId),
        platformApi.listProjectTranscripts(parsedProjectId),
        platformApi.listProjectStoryOutlines(parsedProjectId),
        platformApi.listProjectStoryChapters(parsedProjectId),
        platformApi.listProjectBooks(parsedProjectId),
        platformApi.listProjectReviews(parsedProjectId),
        platformApi.listProjectReviewComments(parsedProjectId),
        platformApi.getProjectReviewCommentStatusCounts(parsedProjectId),
      ]);
      setProject(projectData);
      setUsers(userData);
      setMemberships(membershipData);
      setAuditEvents(auditData);
      setArtifacts(artifactData);
      setInterviewSessions(sessionData);
      setZoomLinkDrafts(
        Object.fromEntries(sessionData.map((session) => [session.id, session.meeting_link || '']))
      );
      setTranscripts(transcriptData);
      setStoryOutlines(storyOutlineData);
      setStoryChapters(storyChapterData);
      setBooks(bookData);
      setReviews(reviewData);
      setReviewComments(reviewCommentData);
      setReviewCommentStatusCounts(reviewCommentStatusCountData);
      setAssignUserId(userData[0]?.id.toString() || '');
      setTranscriptForm((current) => ({
        ...current,
        interviewSessionId: current.interviewSessionId || sessionData[0]?.id.toString() || '',
        language: current.language || projectData.primary_language_code || 'en',
      }));
      const firstReviewTarget =
        (storyChapterData[0] && { type: 'story_chapter', id: storyChapterData[0].id.toString() }) ||
        (storyOutlineData[0] && { type: 'story_outline', id: storyOutlineData[0].id.toString() }) ||
        (transcriptData[0] && { type: 'transcript', id: transcriptData[0].id.toString() }) ||
        (bookData[0] && { type: 'book', id: bookData[0].id.toString() }) ||
        (artifactData[0] && { type: 'artifact', id: artifactData[0].id.toString() });
      setReviewCommentForm((current) => ({
        ...current,
        targetType: current.targetId ? current.targetType : firstReviewTarget?.type || current.targetType,
        authorUserId: current.authorUserId || projectData.owner_id?.toString() || userData[0]?.id.toString() || '',
        targetId: current.targetId || firstReviewTarget?.id || '',
      }));
      setReviewRequestForm((current) => ({
        ...current,
        targetType: current.targetId ? current.targetType : firstReviewTarget?.type || current.targetType,
        reviewerId: current.reviewerId || projectData.owner_id?.toString() || userData[0]?.id.toString() || '',
        targetId: current.targetId || firstReviewTarget?.id || '',
      }));
      setEditor({
        title: projectData.title,
        description: projectData.description || '',
        status: projectData.status,
        storyType: projectData.story_type || 'life_book',
        packageType: projectData.package_type || 'guided',
        language: projectData.primary_language_code,
      });
    } catch (err) {
      setError(t('couldNotLoadProjectWorkspace'));
    } finally {
      setLoading(false);
    }
  }, [parsedProjectId, t]);

  useEffect(() => {
    loadProject();
  }, [loadProject]);

  const bookWorkflowRequestRef = useRef<number | null>(null);

  const loadBookWorkflow = useCallback(
    async (bookId: number) => {
      if (!Number.isFinite(parsedProjectId) || !Number.isFinite(bookId)) {
        return;
      }
      bookWorkflowRequestRef.current = bookId;
      try {
        const [versionData, exportData, publishingRequestData] = await Promise.all([
          platformApi.listProjectBookVersions(parsedProjectId, bookId),
          platformApi.listProjectBookExports(parsedProjectId, bookId),
          platformApi.listProjectPublishingRequests(parsedProjectId, bookId),
        ]);
        // Discard this response if a newer book selection has superseded it,
        // otherwise a slow fetch for a previously selected book can land after
        // (and overwrite) the correct data for the book the user is now on.
        if (bookWorkflowRequestRef.current !== bookId) {
          return;
        }
        setBookVersions(versionData);
        setBookExports(exportData);
        setPublishingRequests(publishingRequestData);
      } catch (err) {
        if (bookWorkflowRequestRef.current === bookId) {
          setError(t('couldNotLoadBookWorkflow'));
        }
      }
    },
    [parsedProjectId, t]
  );

  const assignedUserIds = useMemo(() => {
    return new Set(memberships.map((membership) => membership.user_id));
  }, [memberships]);

  const assignableUsers = useMemo(() => {
    return users.filter((user) => !assignedUserIds.has(user.id) && user.status === 'active');
  }, [assignedUserIds, users]);

  useEffect(() => {
    if (assignableUsers.length > 0 && !assignableUsers.some((user) => user.id.toString() === assignUserId)) {
      setAssignUserId(assignableUsers[0].id.toString());
    }
  }, [assignUserId, assignableUsers]);

  const owner = useMemo(() => {
    if (!project?.owner_id) {
      return undefined;
    }
    return users.find((user) => user.id === project.owner_id);
  }, [project, users]);

  const workflowLabel = (key: string, fallback: string) => t(key === 'publishing' ? 'publishingStage' : key) || fallback;
  const formatWorkspaceDate = useCallback(
    (value?: string | null) => (value ? new Date(value).toLocaleString(locale) : t('notRecorded')),
    [locale, t],
  );

  const storyDocuments = useMemo(() => {
    return artifacts.filter((artifact) => artifact.artifact_type === 'story_document');
  }, [artifacts]);

  const photoArtifacts = useMemo(() => {
    return artifacts.filter(
      (artifact) => artifact.artifact_type === 'photo' || artifact.content_type?.startsWith('image/')
    );
  }, [artifacts]);

  const activeDesignUserId = useMemo(() => {
    if (currentUser?.id) {
      return currentUser.id;
    }
    const formUserId = Number(bookForm.createdByUserId);
    if (Number.isFinite(formUserId) && formUserId > 0) {
      return formUserId;
    }
    return project?.owner_id ?? undefined;
  }, [bookForm.createdByUserId, currentUser?.id, project?.owner_id]);

  const bookDesignDrafts = useMemo(() => {
    return books.filter((book) => isEditableBookStatus(book.status));
  }, [books]);

  const assignedBookDesignDrafts = useMemo(() => {
    if (!activeDesignUserId) {
      return bookDesignDrafts;
    }
    return bookDesignDrafts.filter((book) => book.created_by_user_id === activeDesignUserId);
  }, [activeDesignUserId, bookDesignDrafts]);

  const currentBook = useMemo(() => {
    if (!selectedBookId) {
      return undefined;
    }
    return books.find((book) => book.id.toString() === selectedBookId);
  }, [books, selectedBookId]);

  useEffect(() => {
    if (!project || bookDesignDrafts.length === 0) {
      setSelectedBookId('');
      setBookVersions([]);
      setBookExports([]);
      setPublishingRequests([]);
      return;
    }
    if (bookDesignDrafts.some((book) => book.id.toString() === selectedBookId)) {
      return;
    }
    const storageKey = getLastBookDesignDraftStorageKey(project.id, activeDesignUserId);
    const storedDraftId = window.localStorage.getItem(storageKey);
    const storedDraft = assignedBookDesignDrafts.find((book) => book.id.toString() === storedDraftId);
    const newestAssignedDraft = assignedBookDesignDrafts[assignedBookDesignDrafts.length - 1];
    setSelectedBookId((storedDraft || newestAssignedDraft)?.id.toString() || '');
  }, [activeDesignUserId, assignedBookDesignDrafts, bookDesignDrafts, project, selectedBookId]);

  useEffect(() => {
    if (!selectedBookId) {
      setBookVersions([]);
      setBookExports([]);
      setPublishingRequests([]);
      return;
    }
    loadBookWorkflow(Number(selectedBookId));
  }, [loadBookWorkflow, selectedBookId]);

  useEffect(() => {
    if (!project || !selectedBookId) {
      return;
    }
    window.localStorage.setItem(getLastBookDesignDraftStorageKey(project.id, activeDesignUserId), selectedBookId);
  }, [activeDesignUserId, project, selectedBookId]);

  useEffect(() => {
    if (currentBook) {
      const sourceArtifact = storyDocuments.find((artifact) => artifact.id === currentBook.source_artifact_id);
      setBookForm(bookToDesignForm(currentBook, project, sourceArtifact));
      return;
    }
    setBookForm(createBookDesignForm(project, storyDocuments[0]));
  }, [currentBook, project, storyDocuments]);

  const completedSessionCount = useMemo(() => {
    return interviewSessions.filter((session) => session.status === 'completed').length;
  }, [interviewSessions]);

  const transcriptBySession = useMemo(() => {
    return transcripts.reduce<Record<number, Transcript[]>>((grouped, transcript) => {
      grouped[transcript.interview_session_id] = grouped[transcript.interview_session_id] || [];
      grouped[transcript.interview_session_id].push(transcript);
      return grouped;
    }, {});
  }, [transcripts]);

  const reviewTargetOptions = useMemo(() => {
    const options = [
      ...storyChapters.map((chapter) => ({
        type: 'story_chapter',
        id: chapter.id,
        label: `Chapter: ${chapter.title}`,
      })),
      ...storyOutlines.map((outline) => ({
        type: 'story_outline',
        id: outline.id,
        label: `Outline: ${outline.title}`,
      })),
      ...transcripts.map((transcript) => {
        const session = interviewSessions.find((item) => item.id === transcript.interview_session_id);
        return {
          type: 'transcript',
          id: transcript.id,
          label: `Transcript: ${session?.interview_title || transcript.text.slice(0, 32)}`,
        };
      }),
      ...artifacts
        .filter((artifact) => artifact.text_content || artifact.artifact_type === 'story_document')
        .map((artifact) => ({
          type: 'artifact',
          id: artifact.id,
          label: `${artifact.artifact_type === 'story_document' ? 'Story Document' : 'Artifact'}: ${artifact.title}`,
        })),
      ...books.map((book) => ({
        type: 'book',
        id: book.id,
        label: `Book: ${book.title}`,
      })),
      ...bookExports.map((bookExport) => ({
        type: 'book_export',
        id: bookExport.id,
        label: `Book Export: ${formatTranslatedLabel(t, bookExport.export_type)} #${bookExport.id}`,
      })),
    ];
    return options;
  }, [artifacts, bookExports, books, interviewSessions, storyChapters, storyOutlines, t, transcripts]);

  const commentableReviewTargetOptions = useMemo(() => {
    return reviewTargetOptions.filter((option) => {
      if (option.type !== 'artifact') {
        return true;
      }
      const artifact = artifacts.find((item) => item.id === option.id);
      return Boolean(artifact?.text_content);
    });
  }, [artifacts, reviewTargetOptions]);

  const visibleReviewComments = useMemo(() => {
    if (reviewCommentStatusFilter === 'all') {
      return reviewComments;
    }
    return reviewComments.filter((comment) => comment.status === reviewCommentStatusFilter);
  }, [reviewCommentStatusFilter, reviewComments]);

  const handleSaveProject = async () => {
    if (!project || !editor) {
      return;
    }
    const selectedLanguage = languageOptions.find((option) => option.value === editor.language);
    if (!editor.title.trim() || !selectedLanguage) {
      setError(t('projectTitleAndLanguageRequired'));
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await platformApi.updateProject(project.id, {
        title: editor.title.trim(),
        description: editor.description.trim(),
        status: editor.status,
        story_type: editor.storyType,
        package_type: editor.packageType,
        primary_language_code: selectedLanguage.value,
        locale: selectedLanguage.locale,
        text_direction: selectedLanguage.direction,
      });
      await loadProject();
    } catch (err) {
      setError(t('couldNotSaveProject'));
    } finally {
      setSaving(false);
    }
  };

  const handleAssignMember = async (): Promise<boolean> => {
    if (!project || !assignUserId) {
      return false;
    }

    try {
      setSaving(true);
      setError(null);
      await platformApi.assignProjectMember(project.id, Number(assignUserId), assignRole);
      await loadProject();
      return true;
    } catch (err) {
      setError(t('couldNotAssignProjectMember'));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleUnassignMember = async (membershipId: number) => {
    if (!project) {
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await platformApi.unassignProjectMember(project.id, membershipId);
      await loadProject();
    } catch (err) {
      setError(t('couldNotUnassignProjectMember'));
    } finally {
      setSaving(false);
    }
  };

  const handleCreateArtifact = async () => {
    if (!project || !artifactForm.title.trim()) {
      setArtifactUploadError(t('artifactTitleRequired'));
      setError(null);
      return false;
    }
    if (!artifactFile) {
      setArtifactUploadError(t('noFileSelected'));
      setError(null);
      return false;
    }

    try {
      setSaving(true);
      setError(null);
      setArtifactUploadError(null);
      const uploaderId = currentUser?.id;

      if (artifactForm.artifactType === 'story_document') {
        await platformApi.uploadProjectStoryDocument(project.id, {
          file: artifactFile,
          title: artifactForm.title.trim(),
          description: artifactForm.description.trim() || undefined,
          uploader_user_id: uploaderId,
        });
      } else {
        await platformApi.uploadProjectArtifact(project.id, {
          artifact_type: artifactForm.artifactType,
          file: artifactFile,
          title: artifactForm.title.trim(),
          description: artifactForm.description.trim() || undefined,
          uploader_user_id: uploaderId,
        });
      }

      setArtifactForm(initialArtifactForm);
      setArtifactFile(null);
      setArtifactUploadError(null);
      await loadProject();
      return true;
    } catch (err) {
      setArtifactUploadError(getRequestErrorMessage(err, t('couldNotSaveArtifact')));
      setError(null);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSendStoryDocumentToDesign = async (artifact: Artifact) => {
    if (!project) {
      return;
    }
    try {
      setSaving(true);
      setError(null);
      await platformApi.sendStoryDocumentToDesign(project.id, artifact.id);
      await loadProject();
    } catch (err) {
      setError(t('couldNotSendStoryDocumentToDesign'));
    } finally {
      setSaving(false);
    }
  };

  const handleSendStoryDocumentToPublishing = async (artifact: Artifact) => {
    if (!project) {
      return;
    }
    try {
      setSaving(true);
      setError(null);
      await platformApi.sendStoryDocumentToPublishing(project.id, artifact.id);
      await loadProject();
    } catch (err) {
      setError(t('couldNotSendStoryDocumentToPublishing'));
    } finally {
      setSaving(false);
    }
  };

  const handleCreateBookDesignDraft = async (input: BookDesignDraftCreateInput): Promise<boolean> => {
    if (!project) {
      return false;
    }
    const draftName = input.name.trim();
    if (!draftName) {
      setError(t('bookDesignDraftNameRequired'));
      return false;
    }

    try {
      setSaving(true);
      setError(null);
      const sourceArtifact = input.sourceArtifactId
        ? storyDocuments.find((artifact) => artifact.id === input.sourceArtifactId)
        : storyDocuments[0];
      let draftForm = applyBookTypeDefaults(createBookDesignForm(project, sourceArtifact), input.bookType);
      if (input.template) {
        draftForm = applyDesignSettingsToForm(draftForm, input.template.design_settings_json, {
          language: input.template.language || undefined,
          textDirection: input.template.text_direction || undefined,
        });
      }
      draftForm = {
        ...draftForm,
        title: draftName,
        bookType: input.bookType,
        createdByUserId: activeDesignUserId?.toString() || draftForm.createdByUserId,
      };
      const designSettings = buildBookDesignSettings(draftForm);
      const createdBook = await platformApi.createProjectBook(project.id, {
        source_artifact_id: draftForm.sourceArtifactId ? Number(draftForm.sourceArtifactId) : undefined,
        created_by_user_id: activeDesignUserId,
        title: draftName,
        subtitle: draftForm.subtitle.trim() || undefined,
        author_name: draftForm.authorName.trim() || undefined,
        language: draftForm.language,
        text_direction: draftForm.textDirection,
        book_type: draftForm.bookType,
        manuscript_json: buildBookManuscript(draftForm, null),
        design_settings_json: designSettings,
        cover_json: buildBookCover(draftForm),
        metadata_json: {
          design_draft: true,
          draft_name: draftName,
          created_by_user_id: activeDesignUserId,
          created_at: new Date().toISOString(),
          design_template_id: input.template?.id,
          design_template_name: input.template?.name,
          design_parameters: {
            book_type: draftForm.bookType,
            design_settings_json: designSettings,
            manuscript_json: buildBookManuscript(draftForm, null),
            cover_json: buildBookCover(draftForm),
          },
        },
      });
      setSelectedBookId(createdBook.id.toString());
      window.localStorage.setItem(
        getLastBookDesignDraftStorageKey(project.id, activeDesignUserId),
        createdBook.id.toString()
      );
      await loadProject();
      await loadBookWorkflow(createdBook.id);
      return true;
    } catch (err) {
      setError(t('couldNotCreateBookDesignDraft'));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBookDesignDraft = async (bookId: number): Promise<boolean> => {
    if (!project) {
      return false;
    }
    try {
      setSaving(true);
      setError(null);
      await platformApi.deleteProjectBook(project.id, bookId);
      if (selectedBookId === bookId.toString()) {
        setSelectedBookId('');
        window.localStorage.removeItem(getLastBookDesignDraftStorageKey(project.id, activeDesignUserId));
      }
      await loadProject();
      return true;
    } catch (err) {
      setError(t('couldNotDeleteBookDesignDraft'));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBook = async () => {
    if (!project || !currentBook || !bookForm.title.trim()) {
      setError(t('selectBookAndEnterTitle'));
      return;
    }
    if (!isEditableBookStatus(currentBook.status)) {
      setError(t('bookLockedForEditing'));
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await platformApi.updateProjectBook(project.id, currentBook.id, {
        source_artifact_id: bookForm.sourceArtifactId ? Number(bookForm.sourceArtifactId) : undefined,
        title: bookForm.title.trim(),
        subtitle: bookForm.subtitle.trim() || undefined,
        author_name: bookForm.authorName.trim() || undefined,
        language: bookForm.language,
        text_direction: bookForm.textDirection,
        manuscript_json: buildBookManuscript(bookForm, currentBook),
        design_settings_json: buildBookDesignSettings(bookForm),
        cover_json: buildBookCover(bookForm),
      });
      await loadProject();
      await loadBookWorkflow(currentBook.id);
    } catch (err) {
      setError(t('couldNotSaveBookNeedsDraft'));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBookVersion = async () => {
    if (!project || !currentBook) {
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await platformApi.createProjectBookVersion(project.id, currentBook.id, {
        created_by_user_id: bookForm.createdByUserId ? Number(bookForm.createdByUserId) : undefined,
        change_note: bookForm.versionNote.trim() || undefined,
      });
      setBookForm((current) => ({ ...current, versionNote: '' }));
      await loadBookWorkflow(currentBook.id);
    } catch (err) {
      setError(t('couldNotSaveBookVersion'));
    } finally {
      setSaving(false);
    }
  };

  const handleApproveBook = async () => {
    if (!project || !currentBook) {
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await platformApi.approveProjectBook(project.id, currentBook.id);
      await loadProject();
      await loadBookWorkflow(currentBook.id);
    } catch (err) {
      setError(t('couldNotApproveBookResolveComments'));
    } finally {
      setSaving(false);
    }
  };

  const handleCreateBookExport = async () => {
    if (!project || !currentBook) {
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const createdExport = await platformApi.createProjectBookExport(project.id, currentBook.id, {
        requested_by_user_id: bookForm.createdByUserId ? Number(bookForm.createdByUserId) : undefined,
        export_type: bookForm.exportType,
        print_profile_json: buildBookPrintProfile(currentBook, bookForm),
      });
      await loadProject();
      await loadBookWorkflow(currentBook.id);
      if (createdExport.status === 'failed') {
        setError(getBookExportFailureMessage(createdExport, t('couldNotRequestExportApproveBook')));
      }
    } catch (err) {
      setError(getRequestErrorMessage(err, t('couldNotRequestExportApproveBook')));
    } finally {
      setSaving(false);
    }
  };

  const handleRefreshBookPreview = async () => {
    if (!project || !currentBook) {
      return;
    }
    if (!isEditableBookStatus(currentBook.status)) {
      setError(t('bookLockedForEditing'));
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await platformApi.updateProjectBook(project.id, currentBook.id, {
        source_artifact_id: bookForm.sourceArtifactId ? Number(bookForm.sourceArtifactId) : undefined,
        title: bookForm.title.trim(),
        subtitle: bookForm.subtitle.trim() || undefined,
        author_name: bookForm.authorName.trim() || undefined,
        language: bookForm.language,
        text_direction: bookForm.textDirection,
        book_type: bookForm.bookType,
        manuscript_json: buildBookManuscript(bookForm, currentBook),
        design_settings_json: buildBookDesignSettings(bookForm),
        cover_json: buildBookCover(bookForm),
      });
      const previewExport = await platformApi.createProjectBookPreviewExport(project.id, currentBook.id, {
        requested_by_user_id: bookForm.createdByUserId ? Number(bookForm.createdByUserId) : undefined,
        export_type: 'renderer_preview',
        print_profile_json: buildBookPrintProfile(currentBook, bookForm),
        metadata_json: {
          refreshed_from_workspace: true,
        },
      });
      await loadProject();
      await loadBookWorkflow(currentBook.id);
      if (previewExport.status === 'failed') {
        setError(getBookExportFailureMessage(previewExport, t('couldNotRefreshRendererPreview')));
      }
    } catch (err) {
      setError(getRequestErrorMessage(err, t('couldNotRefreshRendererPreview')));
    } finally {
      setSaving(false);
    }
  };

  const handleApplyDesignParameters = async () => {
    if (!project || !currentBook) {
      return;
    }
    if (!isEditableBookStatus(currentBook.status)) {
      // Do not silently re-render stale settings: the book is locked, so the
      // parameter change was never persisted and the preview must not appear
      // to have refreshed with it applied.
      setError(t('bookLockedForEditing'));
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await platformApi.updateProjectBook(project.id, currentBook.id, {
        source_artifact_id: bookForm.sourceArtifactId ? Number(bookForm.sourceArtifactId) : undefined,
        title: bookForm.title.trim(),
        subtitle: bookForm.subtitle.trim() || undefined,
        author_name: bookForm.authorName.trim() || undefined,
        language: bookForm.language,
        text_direction: bookForm.textDirection,
        manuscript_json: buildBookManuscript(bookForm, currentBook),
        design_settings_json: buildBookDesignSettings(bookForm),
        cover_json: buildBookCover(bookForm),
      });
      const previewExport = await platformApi.createProjectBookPreviewExport(project.id, currentBook.id, {
        requested_by_user_id: bookForm.createdByUserId ? Number(bookForm.createdByUserId) : undefined,
        export_type: 'renderer_preview',
        print_profile_json: buildBookPrintProfile(currentBook, bookForm),
        metadata_json: {
          refreshed_from_workspace: true,
        },
      });
      await loadProject();
      await loadBookWorkflow(currentBook.id);
      if (previewExport.status === 'failed') {
        setError(getBookExportFailureMessage(previewExport, t('couldNotRefreshRendererPreview')));
      }
    } catch (err) {
      setError(getRequestErrorMessage(err, t('couldNotSaveBookNeedsDraft')));
    } finally {
      setSaving(false);
    }
  };

  const handleMarkExportPrintReady = async (bookExport: BookExport) => {
    if (!project || !currentBook) {
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const preflightedExport = await platformApi.preflightProjectBookExport(project.id, currentBook.id, bookExport.id);
      await loadBookWorkflow(currentBook.id);
      if (preflightedExport.preflight_status !== 'passed') {
        setError(t('exportFailedPreflightReviewReport'));
      }
    } catch (err) {
      setError(t('couldNotRunExportPreflight'));
    } finally {
      setSaving(false);
    }
  };

  const handleCreatePublishingRequest = async (bookExport: BookExport) => {
    if (!project || !currentBook) {
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await platformApi.createProjectPublishingRequest(project.id, currentBook.id, {
        export_id: bookExport.id,
        requested_by_user_id: bookForm.createdByUserId ? Number(bookForm.createdByUserId) : undefined,
        provider: bookForm.publishingProvider.trim() || undefined,
        notes: bookForm.publishingNotes.trim() || undefined,
      });
      await loadProject();
      await loadBookWorkflow(currentBook.id);
    } catch (err) {
      setError(t('couldNotSendExportToPrinting'));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePublishingRequest = async (
    publishingRequest: PublishingRequest,
    payload: PublishingRequestUpdatePayload
  ) => {
    if (!project || !currentBook) {
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await platformApi.updateProjectPublishingRequest(project.id, currentBook.id, publishingRequest.id, payload);
      await loadProject();
      await loadBookWorkflow(currentBook.id);
    } catch (err) {
      setError(t('couldNotUpdatePublishingSubmission'));
    } finally {
      setSaving(false);
    }
  };

  const handleCreateInterviewSession = async () => {
    if (!project || !interviewSessionForm.title.trim() || !interviewSessionForm.intervieweeName.trim()) {
      setError(t('interviewTitleAndIntervieweeRequired'));
      return;
    }
    if (interviewSessionForm.meetingLink.trim() && !isZoomMeetingLink(interviewSessionForm.meetingLink.trim())) {
      setError(t('zoomMeetingLinkMustBeHttps'));
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await platformApi.createProjectInterviewSession(project.id, {
        title: interviewSessionForm.title.trim(),
        interview_type: interviewSessionForm.meetingLink.trim() ? 'video' : interviewSessionForm.interviewType,
        interviewer_name: interviewSessionForm.interviewerName.trim() || owner?.display_name || owner?.email || 'Interviewer',
        interviewee_name: interviewSessionForm.intervieweeName.trim(),
        scheduled_at: interviewSessionForm.scheduledAt
          ? new Date(interviewSessionForm.scheduledAt).toISOString()
          : undefined,
        max_duration: Number(interviewSessionForm.maxDuration) || 60,
        notes: interviewSessionForm.notes.trim() || undefined,
        meeting_link: interviewSessionForm.meetingLink.trim() || undefined,
      });
      setInterviewSessionForm(initialInterviewSessionForm);
      await loadProject();
    } catch (err) {
      setError(t('couldNotCreateInterviewSession'));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveZoomSession = async (session: ProjectInterviewSession) => {
    if (!project) {
      return;
    }
    const meetingLink = (zoomLinkDrafts[session.id] || '').trim();
    if (!meetingLink) {
      setError(t('zoomMeetingLinkRequired'));
      return;
    }
    if (!isZoomMeetingLink(meetingLink)) {
      setError(t('zoomMeetingLinkMustBeHttps'));
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await platformApi.configureInterviewStudioZoomSession(project.id, session.id, {
        meeting_link: meetingLink,
        status: 'ready',
      });
      await loadProject();
    } catch (err) {
      setError(t('couldNotSaveZoomMeetingLink'));
    } finally {
      setSaving(false);
    }
  };

  const handleCreateTranscript = async () => {
    if (!project || !transcriptForm.interviewSessionId || !transcriptForm.text.trim()) {
      setError(t('transcriptSessionAndTextRequired'));
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const payload = {
        language: transcriptForm.language.trim() || project.primary_language_code || 'en',
        confidence: transcriptForm.confidence ? Number(transcriptForm.confidence) : undefined,
        text: transcriptForm.text.trim(),
      };
      if (editingTranscriptId) {
        await platformApi.updateProjectTranscript(project.id, editingTranscriptId, {
          ...payload,
          status: 'corrected',
          change_note: 'Edited in project workspace',
        });
      } else {
        await platformApi.createProjectTranscript(project.id, {
          ...payload,
          interview_session_id: Number(transcriptForm.interviewSessionId),
          source_artifact_id: transcriptForm.sourceArtifactId ? Number(transcriptForm.sourceArtifactId) : undefined,
          created_by_user_id: project.owner_id || undefined,
        });
      }
      setTranscriptForm({
        ...initialTranscriptForm,
        interviewSessionId: transcriptForm.interviewSessionId,
        language: transcriptForm.language || project.primary_language_code || 'en',
      });
      setEditingTranscriptId(null);
      await loadProject();
    } catch (err) {
      setError(t('couldNotSaveTranscript'));
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateTranscript = async () => {
    if (!project || !transcriptForm.interviewSessionId || !transcriptForm.sourceArtifactId) {
      setError(t('transcriptSessionAndSourceArtifactRequired'));
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await platformApi.generateProjectTranscript(project.id, {
        interview_session_id: Number(transcriptForm.interviewSessionId),
        source_artifact_id: Number(transcriptForm.sourceArtifactId),
        language: transcriptForm.language.trim() || project.primary_language_code || 'en',
        created_by_user_id: project.owner_id || undefined,
      });
      setTranscriptForm({
        ...initialTranscriptForm,
        interviewSessionId: transcriptForm.interviewSessionId,
        language: transcriptForm.language || project.primary_language_code || 'en',
      });
      setEditingTranscriptId(null);
      await loadProject();
    } catch (err) {
      setError(t('couldNotGenerateTranscript'));
    } finally {
      setSaving(false);
    }
  };

  const handleEditTranscript = (transcript: Transcript) => {
    setEditingTranscriptId(transcript.id);
    setTranscriptForm({
      interviewSessionId: transcript.interview_session_id.toString(),
      sourceArtifactId: transcript.source_artifact_id?.toString() || '',
      language: transcript.language,
      confidence: transcript.confidence != null ? transcript.confidence.toString() : '',
      text: transcript.text,
    });
    setActiveTab('interview-production');
  };

  const handleApproveTranscript = async (transcript: Transcript) => {
    if (!project) {
      return;
    }
    try {
      setSaving(true);
      setError(null);
      await platformApi.approveProjectTranscript(project.id, transcript.id);
      await loadProject();
    } catch (err) {
      setError(t('couldNotApproveTranscript'));
    } finally {
      setSaving(false);
    }
  };

  const handleCreateReviewComment = async () => {
    if (!project || !reviewCommentForm.targetId || !reviewCommentForm.commentText.trim()) {
      setError(t('reviewTargetAndCommentRequired'));
      return;
    }
    if (!reviewCommentForm.anchorText.trim() && (!reviewCommentForm.anchorStart || !reviewCommentForm.anchorEnd)) {
      setError(t('reviewCommentsNeedAnchor'));
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await platformApi.createProjectReviewComment(project.id, {
        target_type: reviewCommentForm.targetType,
        target_id: Number(reviewCommentForm.targetId),
        author_type: reviewCommentForm.authorType,
        author_user_id:
          reviewCommentForm.authorType === 'user' && reviewCommentForm.authorUserId
            ? Number(reviewCommentForm.authorUserId)
            : undefined,
        anchor_text: reviewCommentForm.anchorText.trim() || undefined,
        anchor_start: reviewCommentForm.anchorStart ? Number(reviewCommentForm.anchorStart) : undefined,
        anchor_end: reviewCommentForm.anchorEnd ? Number(reviewCommentForm.anchorEnd) : undefined,
        comment_text: reviewCommentForm.commentText.trim(),
      });
      setReviewCommentForm({
        ...initialReviewCommentForm,
        targetType: reviewCommentForm.targetType,
        targetId: reviewCommentForm.targetId,
        authorUserId: reviewCommentForm.authorUserId,
      });
      await loadProject();
    } catch (err) {
      setError(t('couldNotAddReviewComment'));
    } finally {
      setSaving(false);
    }
  };

  const handleReviewCommentLifecycle = async (
    comment: ReviewComment,
    action: 'resolve' | 'reopen' | 'dismiss'
  ) => {
    if (!project) {
      return;
    }
    try {
      setSaving(true);
      setError(null);
      if (action === 'resolve') {
        await platformApi.resolveProjectReviewComment(project.id, comment.id);
      } else if (action === 'reopen') {
        await platformApi.reopenProjectReviewComment(project.id, comment.id);
      } else {
        await platformApi.dismissProjectReviewComment(project.id, comment.id);
      }
      await loadProject();
    } catch (err) {
      setError(t(`couldNot${action.charAt(0).toUpperCase()}${action.slice(1)}ReviewComment`));
    } finally {
      setSaving(false);
    }
  };

  const handleCreateReview = async () => {
    if (!project || !reviewRequestForm.targetId) {
      setError(t('reviewTargetRequired'));
      return;
    }
    try {
      setSaving(true);
      setError(null);
      await platformApi.createProjectReview(project.id, {
        target_type: reviewRequestForm.targetType,
        target_id: Number(reviewRequestForm.targetId),
        reviewer_id: reviewRequestForm.reviewerId ? Number(reviewRequestForm.reviewerId) : undefined,
        requested_by_user_id: project.owner_id || undefined,
        title: reviewRequestForm.title.trim() || undefined,
        content: reviewRequestForm.content.trim() || undefined,
      });
      setReviewRequestForm({
        ...initialReviewRequestForm,
        targetType: reviewRequestForm.targetType,
        targetId: reviewRequestForm.targetId,
        reviewerId: reviewRequestForm.reviewerId,
      });
      await loadProject();
    } catch (err) {
      setError(t('couldNotRequestReview'));
    } finally {
      setSaving(false);
    }
  };

  const handleStartReview = async (review: Review) => {
    if (!project) {
      return;
    }
    try {
      setSaving(true);
      setError(null);
      await platformApi.startProjectReview(project.id, review.id);
      await loadProject();
    } catch (err) {
      setError(t('couldNotStartReview'));
    } finally {
      setSaving(false);
    }
  };

  const handleDecideReview = async (
    review: Review,
    decision: 'approved' | 'changes_requested' | 'rejected'
  ) => {
    if (!project) {
      return;
    }
    try {
      setSaving(true);
      setError(null);
      await platformApi.decideProjectReview(project.id, review.id, {
        decision,
        notes: reviewDecisionNotes[review.id]?.trim() || undefined,
        decided_by_user_id: project.owner_id || undefined,
      });
      setReviewDecisionNotes((current) => {
        const next = { ...current };
        delete next[review.id];
        return next;
      });
      await loadProject();
    } catch (err) {
      setError(t('couldNotRecordReviewDecision'));
    } finally {
      setSaving(false);
    }
  };

  const renderOverview = () => {
    if (!project || !editor) {
      return null;
    }

    return (
      <Grid container spacing={3}>
        <Grid item xs={12} lg={7}>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Stack spacing={2}>
              <TextField
                label={t('projectName')}
                value={editor.title}
                onChange={(event) => setEditor({ ...editor, title: event.target.value })}
                fullWidth
              />
              <TextField
                label={t('goalsAndInstructions')}
                value={editor.description}
                onChange={(event) => setEditor({ ...editor, description: event.target.value })}
                fullWidth
                multiline
                minRows={4}
              />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel id="status-label">{t('status')}</InputLabel>
                    <Select
                      labelId="status-label"
                      label={t('status')}
                      value={editor.status}
                      onChange={(event) => setEditor({ ...editor, status: event.target.value })}
                    >
                      {projectStatuses.map((status) => (
                        <MenuItem key={status} value={status}>
                          {formatTranslatedLabel(t, status)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel id="story-type-label">{t('storyType')}</InputLabel>
                    <Select
                      labelId="story-type-label"
                      label={t('storyType')}
                      value={editor.storyType}
                      onChange={(event) => setEditor({ ...editor, storyType: event.target.value })}
                    >
                      {storyTypes.map((storyType) => (
                        <MenuItem key={storyType.value} value={storyType.value}>
                          {formatTranslatedLabel(t, storyType.value)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel id="package-type-label">{t('package')}</InputLabel>
                    <Select
                      labelId="package-type-label"
                      label={t('package')}
                      value={editor.packageType}
                      onChange={(event) => setEditor({ ...editor, packageType: event.target.value })}
                    >
                      {packageTypes.map((packageType) => (
                        <MenuItem key={packageType.value} value={packageType.value}>
                          {formatTranslatedLabel(t, packageType.value)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel id="language-label">{t('primaryLanguage')}</InputLabel>
                    <Select
                      labelId="language-label"
                      label={t('primaryLanguage')}
                      value={editor.language}
                      onChange={(event) => setEditor({ ...editor, language: event.target.value })}
                    >
                      {languageOptions.map((language) => (
                        <MenuItem key={language.value} value={language.value}>
                          {language.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <Box>
                <Button variant="contained" startIcon={<Save />} onClick={handleSaveProject} disabled={saving}>
                  {t('saveProject')}
                </Button>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('projectSnapshot')}
            </Typography>
            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">{t('status')}</Typography>
                <Chip size="small" label={formatTranslatedLabel(t, project.status)} color={getStatusColor(project.status)} />
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">{t('projectManager')}</Typography>
                <Typography>{owner?.display_name || owner?.email || t('unassigned')}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">{t('language')}</Typography>
                <Typography>
                  {project.primary_language_code}
                  {project.text_direction === 'rtl' ? ' / RTL' : ''}
                </Typography>
              </Stack>
              <Divider />
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <UploadFile />
                  <Typography>{t('artifacts')}</Typography>
                </Stack>
                <Chip size="small" label={artifacts.length.toString()} variant="outlined" />
              </Stack>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Mic />
                  <Typography>{t('interviewSessions')}</Typography>
                </Stack>
                <Chip
                  size="small"
                  label={`${completedSessionCount}/${interviewSessions.length}`}
                  variant="outlined"
                />
              </Stack>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <FactCheck />
                  <Typography>{t('transcripts')}</Typography>
                </Stack>
                <Chip size="small" label={transcripts.length.toString()} variant="outlined" />
              </Stack>
              {workflowStages.slice(3).map((stage) => (
                <Stack key={stage.key} direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    {stage.icon}
                    <Typography>{workflowLabel(stage.key, stage.label)}</Typography>
                  </Stack>
                  <Chip size="small" label={formatTranslatedLabel(t, stage.status)} variant="outlined" />
                </Stack>
              ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  const renderTeam = () => (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
        <Typography variant="h6" component="h2">
          {t('team')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Groups />}
          onClick={() => setAssignTeamDialogOpen(true)}
          disabled={assignableUsers.length === 0 || saving}
        >
          {t('assignTeam')}
        </Button>
      </Stack>

      <Dialog
        open={assignTeamDialogOpen}
        onClose={() => {
          if (!saving) {
            setAssignTeamDialogOpen(false);
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t('assignTeam')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <FormControl fullWidth disabled={assignableUsers.length === 0}>
              <InputLabel id="assign-user-label">{t('user')}</InputLabel>
              <Select
                labelId="assign-user-label"
                label={t('user')}
                value={assignUserId}
                onChange={(event) => setAssignUserId(event.target.value)}
              >
                {assignableUsers.map((user) => (
                  <MenuItem key={user.id} value={user.id.toString()}>
                    {user.display_name || user.email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="assign-role-label">{t('role')}</InputLabel>
              <Select
                labelId="assign-role-label"
                label={t('role')}
                value={assignRole}
                onChange={(event) => setAssignRole(event.target.value)}
              >
                {projectRoles.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    {formatTranslatedLabel(t, role.value)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignTeamDialogOpen(false)} disabled={saving}>
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Groups />}
            onClick={async () => {
              if (await handleAssignMember()) {
                setAssignTeamDialogOpen(false);
              }
            }}
            disabled={!assignUserId || saving}
          >
            {t('assign')}
          </Button>
        </DialogActions>
      </Dialog>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('user')}</TableCell>
              <TableCell>{t('role')}</TableCell>
              <TableCell>{t('status')}</TableCell>
              <TableCell align="right">{t('actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {memberships.map((membership) => (
              <TableRow key={membership.id} hover>
                <TableCell>
                  <Typography fontWeight={700}>{membership.user_display_name || membership.user_email}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {membership.user_email}
                  </Typography>
                </TableCell>
                <TableCell>{formatTranslatedLabel(t, membership.role_name)}</TableCell>
                <TableCell>
                  <Chip size="small" label={membership.status} color={getStatusColor(membership.status)} />
                </TableCell>
                <TableCell align="right">
                  <Button onClick={() => handleUnassignMember(membership.id)} disabled={saving}>
                    {t('unassign')}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

    </Stack>
  );

  const renderArtifacts = () => (
    <ArtifactsTab
      artifactFile={artifactFile}
      artifactForm={artifactForm}
      artifactUploadError={artifactUploadError}
      artifacts={artifacts}
      onCreateArtifact={handleCreateArtifact}
      onSendStoryDocumentToDesign={handleSendStoryDocumentToDesign}
      onSendStoryDocumentToPublishing={handleSendStoryDocumentToPublishing}
      project={project}
      memberships={memberships}
      saving={saving}
      setArtifactFile={setArtifactFile}
      setArtifactForm={setArtifactForm}
      setArtifactUploadError={setArtifactUploadError}
    />
  );

  const renderDesignPublishing = (stage: 'design' | 'publishing') => (
    <DesignPublishingTab
      bookExports={bookExports}
      bookForm={bookForm}
      bookVersions={bookVersions}
      bookDesignDrafts={bookDesignDrafts}
      books={books}
      currentBook={currentBook}
      activeDesignUserId={activeDesignUserId}
      onApproveBook={handleApproveBook}
      onCreateBookDesignDraft={handleCreateBookDesignDraft}
      onCreateBookExport={handleCreateBookExport}
      onDeleteBookDesignDraft={handleDeleteBookDesignDraft}
      onCreatePublishingRequest={handleCreatePublishingRequest}
      onUpdatePublishingRequest={handleUpdatePublishingRequest}
      onMarkExportPrintReady={handleMarkExportPrintReady}
      onRefreshBookPreview={handleRefreshBookPreview}
      onApplyDesignParameters={handleApplyDesignParameters}
      onSaveBook={handleSaveBook}
      onSaveBookVersion={handleSaveBookVersion}
      onSelectBook={setSelectedBookId}
      photoArtifacts={photoArtifacts}
      project={project}
      publishingRequests={publishingRequests}
      saving={saving}
      selectedBookId={selectedBookId}
      setBookForm={setBookForm}
      stage={stage}
      storyChapters={storyChapters}
      storyDocuments={storyDocuments}
      users={users}
    />
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const renderInterviews = () => (
    <Stack spacing={3}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              label={t('interviewTitle')}
              value={interviewSessionForm.title}
              onChange={(event) => setInterviewSessionForm({ ...interviewSessionForm, title: event.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel id="interview-type-label">{t('type')}</InputLabel>
              <Select
                labelId="interview-type-label"
                label={t('type')}
                value={interviewSessionForm.interviewType}
                onChange={(event) =>
                  setInterviewSessionForm({
                    ...interviewSessionForm,
                    interviewType: event.target.value as 'audio' | 'video',
                  })
                }
              >
                <MenuItem value="audio">{t('audio')}</MenuItem>
                <MenuItem value="video">{t('video')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label={t('interviewer')}
              value={interviewSessionForm.interviewerName}
              onChange={(event) => setInterviewSessionForm({ ...interviewSessionForm, interviewerName: event.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label={t('interviewee')}
              value={interviewSessionForm.intervieweeName}
              onChange={(event) => setInterviewSessionForm({ ...interviewSessionForm, intervieweeName: event.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label={t('scheduled')}
              type="datetime-local"
              value={interviewSessionForm.scheduledAt}
              onChange={(event) => setInterviewSessionForm({ ...interviewSessionForm, scheduledAt: event.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label={t('minutes')}
              type="number"
              value={interviewSessionForm.maxDuration}
              onChange={(event) => setInterviewSessionForm({ ...interviewSessionForm, maxDuration: event.target.value })}
              fullWidth
              inputProps={{ min: 1 }}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              label={t('notes')}
              value={interviewSessionForm.notes}
              onChange={(event) => setInterviewSessionForm({ ...interviewSessionForm, notes: event.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              label={t('zoomMeetingLink')}
              value={interviewSessionForm.meetingLink}
              onChange={(event) =>
                setInterviewSessionForm({
                  ...interviewSessionForm,
                  interviewType: event.target.value.trim() ? 'video' : interviewSessionForm.interviewType,
                  meetingLink: event.target.value,
                })
              }
              placeholder="https://your-company.zoom.us/j/..."
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button variant="contained" startIcon={<Add />} onClick={handleCreateInterviewSession} disabled={saving}>
              {t('add')}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('session')}</TableCell>
              <TableCell>{t('participants')}</TableCell>
              <TableCell>{t('scheduled')}</TableCell>
              <TableCell>{t('duration')}</TableCell>
              <TableCell>{t('status')}</TableCell>
              <TableCell>{t('zoom')}</TableCell>
              <TableCell>{t('transcripts')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {interviewSessions.map((session) => (
              <TableRow key={session.id} hover>
                <TableCell>
                  <Typography fontWeight={700}>{session.interview_title}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatTranslatedLabel(t, session.interview_type)} {t('session')} #{session.session_number}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography>{session.interviewee_name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {session.interviewer_name}
                  </Typography>
                </TableCell>
                <TableCell>
                  {session.scheduled_at ? formatWorkspaceDate(session.scheduled_at) : t('notScheduled')}
                </TableCell>
                <TableCell>
                  {session.actual_duration ? `${session.actual_duration} / ` : ''}
                  {session.max_duration || 0} min
                </TableCell>
                <TableCell>
                  <Chip size="small" label={formatTranslatedLabel(t, session.status)} color={getStatusColor(session.status)} />
                </TableCell>
                <TableCell sx={{ minWidth: 280 }}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }}>
                    <TextField
                      size="small"
                      label={t('meetingLink')}
                      value={zoomLinkDrafts[session.id] ?? session.meeting_link ?? ''}
                      onChange={(event) =>
                        setZoomLinkDrafts((current) => ({
                          ...current,
                          [session.id]: event.target.value,
                        }))
                      }
                      fullWidth
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleSaveZoomSession(session)}
                      disabled={saving || !(zoomLinkDrafts[session.id] || '').trim()}
                    >
                      {t('save')}
                    </Button>
                    {session.meeting_link && (
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<OpenInNew />}
                        component="a"
                        href={session.meeting_link}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {t('join')}
                      </Button>
                    )}
                  </Stack>
                </TableCell>
                <TableCell>
                  {transcriptBySession[session.id]?.length
                    ? `${transcriptBySession[session.id].length} ${t('saved')}`
                    : t('pending')}
                </TableCell>
              </TableRow>
            ))}
            {interviewSessions.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography color="text.secondary" align="center" sx={{ py: 5 }}>
                    {t('noInterviewSessionsYet')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const renderTranscripts = () => (
    <Stack spacing={3}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth disabled={interviewSessions.length === 0}>
              <InputLabel id="transcript-session-label">{t('session')}</InputLabel>
              <Select
                labelId="transcript-session-label"
                label={t('session')}
                value={transcriptForm.interviewSessionId}
                onChange={(event) => setTranscriptForm({ ...transcriptForm, interviewSessionId: event.target.value })}
              >
                {interviewSessions.map((session) => (
                  <MenuItem key={session.id} value={session.id.toString()}>
                    {session.interview_title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel id="transcript-artifact-label">{t('sourceArtifact')}</InputLabel>
              <Select
                labelId="transcript-artifact-label"
                label={t('sourceArtifact')}
                value={transcriptForm.sourceArtifactId}
                onChange={(event) => setTranscriptForm({ ...transcriptForm, sourceArtifactId: event.target.value })}
              >
                <MenuItem value="">{t('none')}</MenuItem>
                {artifacts.map((artifact) => (
                  <MenuItem key={artifact.id} value={artifact.id.toString()}>
                    {artifact.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              label={t('language')}
              value={transcriptForm.language}
              onChange={(event) => setTranscriptForm({ ...transcriptForm, language: event.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              label={t('confidence')}
              type="number"
              value={transcriptForm.confidence}
              onChange={(event) => setTranscriptForm({ ...transcriptForm, confidence: event.target.value })}
              fullWidth
              inputProps={{ min: 0, max: 1, step: 0.01 }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreateTranscript}
              disabled={saving || !transcriptForm.interviewSessionId}
              fullWidth
            >
              {editingTranscriptId ? t('update') : t('add')}
            </Button>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="outlined"
              startIcon={<AutoStories />}
              onClick={handleGenerateTranscript}
              disabled={saving || !transcriptForm.interviewSessionId || !transcriptForm.sourceArtifactId}
              fullWidth
            >
              {t('generate')}
            </Button>
          </Grid>
          {editingTranscriptId && (
            <Grid item xs={12} md={2}>
              <Button
                variant="text"
                onClick={() => {
                  setEditingTranscriptId(null);
                  setTranscriptForm(initialTranscriptForm);
                }}
                fullWidth
              >
                {t('cancel')}
              </Button>
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField
              label={t('transcriptText')}
              value={transcriptForm.text}
              onChange={(event) => setTranscriptForm({ ...transcriptForm, text: event.target.value })}
              fullWidth
              multiline
              minRows={4}
            />
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('transcripts')}</TableCell>
              <TableCell>{t('session')}</TableCell>
              <TableCell>{t('source')}</TableCell>
              <TableCell>{t('language')}</TableCell>
              <TableCell>{t('status')}</TableCell>
              <TableCell>{t('created')}</TableCell>
              <TableCell align="right">{t('actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transcripts.map((transcript) => {
              const session = interviewSessions.find((item) => item.id === transcript.interview_session_id);
              const sourceArtifact = artifacts.find((artifact) => artifact.id === transcript.source_artifact_id);
              return (
                <TableRow key={transcript.id} hover>
                  <TableCell>
                    <Typography fontWeight={700}>
                      {transcript.text.length > 80 ? `${transcript.text.slice(0, 80)}...` : transcript.text}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {transcript.confidence != null ? `${Math.round(transcript.confidence * 100)}% ${t('confidence')}` : t('manualTranscript')}
                    </Typography>
                  </TableCell>
                  <TableCell>{session?.interview_title || `Session #${transcript.interview_session_id}`}</TableCell>
                  <TableCell>{sourceArtifact?.title || t('none')}</TableCell>
                  <TableCell>{transcript.language}</TableCell>
                  <TableCell>
                    <Chip size="small" label={formatTranslatedLabel(t, transcript.status)} color={getStatusColor(transcript.status)} />
                  </TableCell>
                  <TableCell>
                    {formatWorkspaceDate(transcript.created_at)}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <IconButton
                        aria-label={t('editTranscript')}
                        onClick={() => handleEditTranscript(transcript)}
                        disabled={saving}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        aria-label={t('approveTranscript')}
                        onClick={() => handleApproveTranscript(transcript)}
                        disabled={saving || transcript.status === 'approved'}
                        color="success"
                      >
                        <AssignmentTurnedIn />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
            {transcripts.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography color="text.secondary" align="center" sx={{ py: 5 }}>
                    {t('noTranscriptsYet')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );

  const renderReviews = () => {
    const filteredCommentTargets = commentableReviewTargetOptions.filter((option) => option.type === reviewCommentForm.targetType);
    const filteredReviewTargets = reviewTargetOptions.filter((option) => option.type === reviewRequestForm.targetType);
    const targetLabel = (targetType: string, targetId: number) =>
      reviewTargetOptions.find((option) => option.type === targetType && option.id === targetId)?.label ||
      `${formatTranslatedLabel(t, targetType)} #${targetId}`;
    const countForStatus = (status: string) => {
      if (status === 'all') {
        return reviewCommentStatusCounts.total;
      }
      return reviewCommentStatusCounts[status as keyof ReviewCommentStatusCounts] || 0;
    };

    return (
      <Stack spacing={3}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel id="review-request-target-type-label">{t('reviewTargetType')}</InputLabel>
                <Select
                  labelId="review-request-target-type-label"
                  label={t('reviewTargetType')}
                  value={reviewRequestForm.targetType}
                  onChange={(event) => {
                    const targetType = event.target.value;
                    const firstTarget = reviewTargetOptions.find((option) => option.type === targetType);
                    setReviewRequestForm({
                      ...reviewRequestForm,
                      targetType,
                      targetId: firstTarget?.id.toString() || '',
                    });
                  }}
                >
                  <MenuItem value="story_chapter">{t('storyChapter')}</MenuItem>
                  <MenuItem value="story_outline">{t('storyOutline')}</MenuItem>
                  <MenuItem value="transcript">{t('transcript')}</MenuItem>
                  <MenuItem value="artifact">{t('artifact')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth disabled={filteredReviewTargets.length === 0}>
                <InputLabel id="review-request-target-label">{t('reviewTarget')}</InputLabel>
                <Select
                  labelId="review-request-target-label"
                  label={t('reviewTarget')}
                  value={reviewRequestForm.targetId}
                  onChange={(event) => setReviewRequestForm({ ...reviewRequestForm, targetId: event.target.value })}
                >
                  {filteredReviewTargets.map((option) => (
                    <MenuItem key={`${option.type}-${option.id}`} value={option.id.toString()}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel id="reviewer-label">{t('reviewer')}</InputLabel>
                <Select
                  labelId="reviewer-label"
                  label={t('reviewer')}
                  value={reviewRequestForm.reviewerId}
                  onChange={(event) => setReviewRequestForm({ ...reviewRequestForm, reviewerId: event.target.value })}
                >
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id.toString()}>
                      {user.display_name || user.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                startIcon={<AssignmentTurnedIn />}
                onClick={handleCreateReview}
                disabled={saving || !reviewRequestForm.targetId}
                fullWidth
              >
                {t('request')}
              </Button>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t('reviewTitle')}
                value={reviewRequestForm.title}
                onChange={(event) => setReviewRequestForm({ ...reviewRequestForm, title: event.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                label={t('reviewInstructions')}
                value={reviewRequestForm.content}
                onChange={(event) => setReviewRequestForm({ ...reviewRequestForm, content: event.target.value })}
                fullWidth
              />
            </Grid>
          </Grid>
        </Paper>

        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('review')}</TableCell>
                <TableCell>{t('target')}</TableCell>
                <TableCell>{t('reviewer')}</TableCell>
                <TableCell>{t('status')}</TableCell>
                <TableCell>{t('decisionNote')}</TableCell>
                <TableCell align="right">{t('actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reviews.map((review) => {
                const reviewer = users.find((user) => user.id === review.reviewer_id);
                const isFinal = review.status === 'approved' || review.status === 'rejected';
                return (
                  <TableRow key={review.id} hover>
                    <TableCell>
                      <Typography fontWeight={700}>{review.title}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {review.content}
                      </Typography>
                      {review.decision_summary && (
                        <Typography variant="body2" color="text.secondary">
                          {t('decision')}: {review.decision_summary}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{review.target_label || targetLabel(review.target_type, review.target_id)}</TableCell>
                    <TableCell>{reviewer?.display_name || reviewer?.email || t('unassigned')}</TableCell>
                    <TableCell>
                      <Chip size="small" label={formatTranslatedLabel(t, review.status)} color={getStatusColor(review.status)} />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={reviewDecisionNotes[review.id] || ''}
                        onChange={(event) =>
                          setReviewDecisionNotes({
                            ...reviewDecisionNotes,
                            [review.id]: event.target.value,
                          })
                        }
                        disabled={saving || isFinal}
                        fullWidth
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        {!isFinal && review.status !== 'in_review' && (
                          <Button
                            size="small"
                            startIcon={<PlayArrow />}
                            onClick={() => handleStartReview(review)}
                            disabled={saving}
                          >
                            {t('start')}
                          </Button>
                        )}
                        {!isFinal && (
                          <>
                            <Button
                              size="small"
                              color="success"
                              startIcon={<ThumbUp />}
                              onClick={() => handleDecideReview(review, 'approved')}
                              disabled={saving}
                            >
                              {t('approve')}
                            </Button>
                            <Button
                              size="small"
                              color="warning"
                              startIcon={<Edit />}
                              onClick={() => handleDecideReview(review, 'changes_requested')}
                              disabled={saving}
                            >
                              {t('changes')}
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              startIcon={<Block />}
                              onClick={() => handleDecideReview(review, 'rejected')}
                              disabled={saving}
                            >
                              {t('reject')}
                            </Button>
                          </>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
              {reviews.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography color="text.secondary" align="center" sx={{ py: 5 }}>
                      {t('noReviewsRequestedYet')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label={`${t('total')} ${reviewCommentStatusCounts.total}`} variant="outlined" />
              <Chip label={`${t('openStatus')} ${reviewCommentStatusCounts.open}`} color={getStatusColor('review_in_progress')} />
              <Chip label={`${t('resolved')} ${reviewCommentStatusCounts.resolved}`} color={getStatusColor('completed')} />
              <Chip label={`${t('dismissed')} ${reviewCommentStatusCounts.dismissed}`} color={getStatusColor('cancelled')} />
            </Stack>
            <ToggleButtonGroup
              color="primary"
              exclusive
              size="small"
              value={reviewCommentStatusFilter}
              onChange={(_, value) => {
                if (value) {
                  setReviewCommentStatusFilter(value);
                }
              }}
              aria-label={t('reviewCommentStatusFilter')}
            >
              {reviewCommentStatuses.map((status) => (
                <ToggleButton key={status} value={status} aria-label={`${formatTranslatedLabel(t, status)} ${t('comments')}`}>
                  {formatTranslatedLabel(t, status)} ({countForStatus(status)})
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel id="review-target-type-label">{t('targetType')}</InputLabel>
                <Select
                  labelId="review-target-type-label"
                  label={t('targetType')}
                  value={reviewCommentForm.targetType}
                  onChange={(event) => {
                    const targetType = event.target.value;
                    const firstTarget = commentableReviewTargetOptions.find((option) => option.type === targetType);
                    setReviewCommentForm({
                      ...reviewCommentForm,
                      targetType,
                      targetId: firstTarget?.id.toString() || '',
                    });
                  }}
                >
                  <MenuItem value="story_chapter">{t('storyChapter')}</MenuItem>
                  <MenuItem value="story_outline">{t('storyOutline')}</MenuItem>
                  <MenuItem value="transcript">{t('transcript')}</MenuItem>
                  <MenuItem value="artifact">{t('textArtifact')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={5}>
              <FormControl fullWidth disabled={filteredCommentTargets.length === 0}>
                <InputLabel id="review-target-label">{t('target')}</InputLabel>
                <Select
                  labelId="review-target-label"
                  label={t('target')}
                  value={reviewCommentForm.targetId}
                  onChange={(event) => setReviewCommentForm({ ...reviewCommentForm, targetId: event.target.value })}
                >
                  {filteredCommentTargets.map((option) => (
                    <MenuItem key={`${option.type}-${option.id}`} value={option.id.toString()}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth>
                <InputLabel id="review-author-type-label">{t('author')}</InputLabel>
                <Select
                  labelId="review-author-type-label"
                  label={t('author')}
                  value={reviewCommentForm.authorType}
                  onChange={(event) =>
                    setReviewCommentForm({
                      ...reviewCommentForm,
                      authorType: event.target.value as 'user' | 'ai',
                    })
                  }
                >
                  <MenuItem value="user">{t('user')}</MenuItem>
                  <MenuItem value="ai">{t('ai')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleCreateReviewComment}
                disabled={saving || !reviewCommentForm.targetId}
                fullWidth
              >
                {t('add')}
              </Button>
            </Grid>
            {reviewCommentForm.authorType === 'user' && (
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel id="review-author-label">{t('user')}</InputLabel>
                  <Select
                    labelId="review-author-label"
                    label={t('user')}
                    value={reviewCommentForm.authorUserId}
                    onChange={(event) => setReviewCommentForm({ ...reviewCommentForm, authorUserId: event.target.value })}
                  >
                    {users.map((user) => (
                      <MenuItem key={user.id} value={user.id.toString()}>
                        {user.display_name || user.email}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} md={reviewCommentForm.authorType === 'user' ? 9 : 12}>
              <TextField
                label={t('selectedText')}
                value={reviewCommentForm.anchorText}
                onChange={(event) => setReviewCommentForm({ ...reviewCommentForm, anchorText: event.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                label={t('start')}
                type="number"
                value={reviewCommentForm.anchorStart}
                onChange={(event) => setReviewCommentForm({ ...reviewCommentForm, anchorStart: event.target.value })}
                fullWidth
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                label={t('end')}
                type="number"
                value={reviewCommentForm.anchorEnd}
                onChange={(event) => setReviewCommentForm({ ...reviewCommentForm, anchorEnd: event.target.value })}
                fullWidth
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                label={t('comment')}
                value={reviewCommentForm.commentText}
                onChange={(event) => setReviewCommentForm({ ...reviewCommentForm, commentText: event.target.value })}
                fullWidth
              />
            </Grid>
          </Grid>
        </Paper>

        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('comment')}</TableCell>
                <TableCell>{t('target')}</TableCell>
                <TableCell>{t('selectedText')}</TableCell>
                <TableCell>{t('author')}</TableCell>
                <TableCell>{t('status')}</TableCell>
                <TableCell align="right">{t('actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleReviewComments.map((comment) => (
                <TableRow key={comment.id} hover>
                  <TableCell>
                    <Typography fontWeight={700}>{comment.comment_text}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatWorkspaceDate(comment.created_at)}
                    </Typography>
                  </TableCell>
                  <TableCell>{targetLabel(comment.target_type, comment.target_id)}</TableCell>
                  <TableCell>
                    <Typography>{comment.anchor_text || t('rangeOnly')}</Typography>
                    {comment.anchor_start != null && comment.anchor_end != null && (
                      <Typography variant="body2" color="text.secondary">
                        {comment.anchor_start}-{comment.anchor_end}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{comment.author_type === 'ai' ? t('ai') : `${t('user')} #${comment.author_user_id || '-'}`}</TableCell>
                  <TableCell>
                    <Chip size="small" label={formatTranslatedLabel(t, comment.status)} color={getStatusColor(comment.status)} />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      {comment.status === 'open' ? (
                        <>
                          <Button
                            size="small"
                            startIcon={<CheckCircle />}
                            onClick={() => handleReviewCommentLifecycle(comment, 'resolve')}
                            disabled={saving}
                          >
                            {t('resolve')}
                          </Button>
                          <Button
                            size="small"
                            color="inherit"
                            startIcon={<Block />}
                            onClick={() => handleReviewCommentLifecycle(comment, 'dismiss')}
                            disabled={saving}
                          >
                            {t('dismiss')}
                          </Button>
                        </>
                      ) : (
                        <Button
                          size="small"
                          startIcon={<Replay />}
                          onClick={() => handleReviewCommentLifecycle(comment, 'reopen')}
                          disabled={saving}
                        >
                          {t('reopen')}
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {visibleReviewComments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography color="text.secondary" align="center" sx={{ py: 5 }}>
                      {reviewCommentStatusFilter === 'all'
                        ? t('noReviewCommentsYet')
                        : `${t('no')} ${formatTranslatedLabel(t, reviewCommentStatusFilter).toLowerCase()} ${t('reviewCommentsYet')}`}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    );
  };

  const renderSettings = () => (
    <Stack spacing={3}>
      {renderOverview()}
      {renderTeam()}
    </Stack>
  );

  const renderInterviewProduction = () => <InterviewStudioTab project={project} />;

  const renderStoryProduction = () => <WritingStudioTab project={project} />;

  const renderActiveTab = () => {
    if (activeTab === 'settings') {
      return renderSettings();
    }
    if (activeTab === 'audit') {
      return <AuditTab auditEvents={auditEvents} />;
    }
    if (activeTab === 'artifacts') {
      return renderArtifacts();
    }
    if (activeTab === 'interview-production') {
      return renderInterviewProduction();
    }
    if (activeTab === 'story-production') {
      return renderStoryProduction();
    }
    if (activeTab === 'reviews') {
      return renderReviews();
    }
    if (activeTab === 'design') {
      return renderDesignPublishing('design');
    }
    if (activeTab === 'publishing') {
      return renderDesignPublishing('publishing');
    }
    return (
      <WorkflowTab
        project={project}
        reviewCommentStatusCounts={reviewCommentStatusCounts}
        stageKey={activeTab}
        storyDocuments={storyDocuments}
      />
    );
  };

  if (loading && !project) {
    return (
      <Box minHeight="360px" display="flex" alignItems="center" justifyContent="center">
        <Typography>{t('loadingProject')}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} mb={2}>
        <Button component={RouterLink} to="/projects" startIcon={<ArrowBack />}>
          {t('projects')}
        </Button>
        <IconButton aria-label={t('refreshProject')} onClick={loadProject}>
          <Refresh />
        </IconButton>
      </Stack>

      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" mb={3}>
        <Typography variant="h4" component="h1">
          {project?.title || t('project')}
        </Typography>
        {project && (
          <Chip size="small" label={formatTranslatedLabel(t, project.status)} color={getStatusColor(project.status)} />
        )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab value="settings" icon={<Folder />} iconPosition="start" label={t('settings')} />
          <Tab value="artifacts" icon={<UploadFile />} iconPosition="start" label={t('assets')} />
          <Tab value="interview-production" icon={<Mic />} iconPosition="start" label={t('interviewsAndTranscripts')} />
          <Tab value="story-production" icon={<AutoStories />} iconPosition="start" label={t('storyStudio')} />
          <Tab value="reviews" icon={<AssignmentTurnedIn />} iconPosition="start" label={t('reviews')} />
          <Tab value="design" icon={<Edit />} iconPosition="start" label={t('design')} />
          <Tab value="publishing" icon={<PlayArrow />} iconPosition="start" label={t('publishing')} />
          <Tab value="audit" icon={<History />} iconPosition="start" label={t('audit')} />
        </Tabs>
      </Paper>

      {renderActiveTab()}
    </Box>
  );
};

export default ProjectWorkspace;
